import React, { useEffect, useState } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronLeft, Share2, Info, CheckCircle2 } from 'lucide-react';
import axios from 'axios';
import { trackWhatsAppClick } from '../../utils/analytics';
import { Button } from '../../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const MenuDetail = () => {
  const { slug } = useParams();
  const [menu, setMenu] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isCopied, setIsCopied] = useState(false);

  useEffect(() => {
    const fetchMenu = async () => {
      try {
        const response = await axios.get(`${API_URL}/menus`);
        const menus = response.data;
        const foundMenu = menus.find((m) => m.slug === slug);
        setMenu(foundMenu);

        if (foundMenu) {
          // --- SEO Optimizations ---
      // Update Title
      document.title = `${foundMenu.name} | Menu Kenari Restaurant Spencer Green Hotel`;

      // Update or Create Meta Description
      let metaDescription = document.querySelector('meta[name="description"]');
      if (!metaDescription) {
        metaDescription = document.createElement('meta');
        metaDescription.name = "description";
        document.head.appendChild(metaDescription);
      }
      metaDescription.setAttribute('content', `Nikmati ${foundMenu.name} yang lezat di Kenari Restaurant, Spencer Green Hotel Batu. ${foundMenu.description.substring(0, 100)}...`);
      
      // Open Graph Tags (Optional but good for SEO/sharing)
      let ogTitle = document.querySelector('meta[property="og:title"]');
      if (!ogTitle) {
        ogTitle = document.createElement('meta');
        ogTitle.setAttribute('property', 'og:title');
        document.head.appendChild(ogTitle);
      }
      ogTitle.setAttribute('content', foundMenu.name);

      let ogImage = document.querySelector('meta[property="og:image"]');
      if (!ogImage) {
        ogImage = document.createElement('meta');
        ogImage.setAttribute('property', 'og:image');
        document.head.appendChild(ogImage);
      }
      ogImage.setAttribute('content', foundMenu.image);
    }
      } catch (error) {
        console.error('Failed to fetch menu detail:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchMenu();
  }, [slug]);

  const handleShare = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: `${menu.name} - Kenari Restaurant`,
          text: `Cek menu lezat ini di Kenari Restaurant: ${menu.name}`,
          url: window.location.href,
        });
      } else {
        await navigator.clipboard.writeText(window.location.href);
        setIsCopied(true);
        setTimeout(() => setIsCopied(false), 2000);
      }
    } catch (error) {
      console.log('Error sharing:', error);
    }
  };

  if (isLoading) {
    return (
      <div className="bg-white min-h-screen pt-24 pb-20 flex justify-center items-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  if (!menu) {
    return <Navigate to="/menus" replace />;
  }

  return (
    <div className="bg-white min-h-screen pt-24 pb-20" data-testid="menu-detail-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Breadcrumb / Back Navigation */}
        <div className="mb-8">
          <Link
            to="/menus"
            className="inline-flex items-center text-sm font-medium text-gray-500 hover:text-emerald-600 transition-colors"
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            Kembali ke Semua Menu
          </Link>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {/* Image Section */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            className="relative rounded-3xl overflow-hidden bg-gray-100 shadow-xl"
          >
            <div className="aspect-[4/3] lg:aspect-square">
              <img
                src={menu.image}
                alt={`Gambar ${menu.name}`}
                className="w-full h-full object-cover"
              />
            </div>
            {menu.isRecommended && (
              <div className="absolute top-6 right-6 bg-amber-500 text-white font-bold px-4 py-2 rounded-full shadow-lg">
                Recommended
              </div>
            )}
          </motion.div>

          {/* Content Section */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="flex flex-col h-full"
          >
            <div className="mb-2 flex items-center justify-between">
              <span className="text-emerald-600 font-semibold tracking-wider text-sm uppercase">
                {menu.category}
              </span>
              <button 
                onClick={handleShare}
                className="text-gray-400 hover:text-emerald-600 transition-colors flex items-center gap-1 text-sm bg-gray-50 px-3 py-1.5 rounded-full"
              >
                {isCopied ? <CheckCircle2 className="w-4 h-4 text-emerald-500" /> : <Share2 className="w-4 h-4" />}
                {isCopied ? 'Tersalin' : 'Bagikan'}
              </button>
            </div>

            <h1 className="font-display text-4xl sm:text-5xl font-semibold text-gray-900 mb-4 leading-tight">
              {menu.name}
            </h1>
            
            <p className="text-3xl font-bold text-emerald-700 mb-8 font-mono">
              {menu.price}
            </p>

            <div className="bg-emerald-50/50 rounded-2xl p-6 mb-8 border border-emerald-100">
              <h3 className="flex items-center text-lg font-semibold text-gray-900 mb-3">
                <Info className="w-5 h-5 mr-2 text-emerald-600" />
                Deskripsi Menu
              </h3>
              <p className="text-gray-600 leading-relaxed text-lg">
                {menu.description}
              </p>
            </div>

            <div className="mt-auto pt-8 border-t border-gray-100">
              <p className="text-gray-500 text-sm mb-4 text-center lg:text-left">
                Tertarik untuk mencicipi hidangan ini? Segera reservasi meja Anda di Kenari Restaurant.
              </p>
              <a
                href={`https://wa.me/6281130700206?text=Hi,%20saya%20ingin%20reservasi%20meja%20di%20Kenari%20Restaurant%20dan%20tertarik%20dengan%20menu%20${encodeURIComponent(menu.name)}`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackWhatsAppClick('Menu Detail CTA')}
                className="w-full inline-flex justify-center"
              >
                <Button size="lg" className="w-full sm:w-auto bg-emerald-600 hover:bg-emerald-700 text-white shadow-lg shadow-emerald-200">
                  Reservasi via WhatsApp
                </Button>
              </a>
            </div>
          </motion.div>
        </div>

      </div>
    </div>
  );
};

export default MenuDetail;
